/**
 * Overview Intro Section Component
 * Displays the main overview with icon, title, description, and expand/collapse
 * Follows Single Responsibility Principle
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { ShowMoreTextButton } from '@/components/ui/show-more-text-button';
import { useTextOverflow } from '@/hooks/useTextOverflow';
import { SectionHeaderWithIcon } from './SectionHeaderWithIcon';

export interface OverviewIntroSectionProps {
  title: string;
  description: string;
  className?: string;
}

export const OverviewIntroSection: React.FC<OverviewIntroSectionProps> = ({
  title,
  description,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { textRef, exceedsLines } = useTextOverflow<HTMLParagraphElement>(
    3,
    description
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.4 },
      }}
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3],
          width: '100%',
        }}
      >
        <SectionHeaderWithIcon
          icon='/assets/images/icons/overview.svg'
          iconAlt='Overview icon'
          title={title}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[1],
          }}
        >
          <motion.p
            ref={textRef}
            animate={{
              WebkitLineClamp: isExpanded ? 'none' : 3,
            }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
            }}
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              fontWeight: typography.weight.regular,
              color: typographyColors.primary,
              lineHeight: typography.lineHeight.relaxed,
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: isExpanded ? 'none' : 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </motion.p>

          {/* Show More/Less Link - Inline with text - only show if text exceeds 3 lines */}
          {exceedsLines && (
            <ShowMoreTextButton
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
};
